import React, { useEffect, useState, useMemo } from 'react';
import { MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../services/api';
import type { AdminFeedbackItem } from '../types';
import { fmtDate } from '../utils/format';

const TYPE_CONFIG = {
  feature: { label: 'Feature',   bg: '#1e1b4b', color: '#818cf8' },
  issue:   { label: 'Bug/Issue', bg: '#450a0a', color: '#f87171' },
};

const STATUS_CONFIG: Record<string, { bg: string; color: string }> = {
  open:        { bg: '#1e293b',  color: '#94a3b8' },
  'in-progress': { bg: '#451a03', color: '#fb923c' },
  completed:   { bg: '#14532d', color: '#4ade80' },
  archived:    { bg: '#1e293b',  color: '#64748b' },
};

function Badge({ bg, color, label }: { bg: string; color: string; label: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, background: bg, color,
    }}>
      {label}
    </span>
  );
}

function FeedbackRow({ item }: { item: AdminFeedbackItem }) {
  const [expanded, setExpanded] = useState(false);
  const typeCfg = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.issue;
  const statusCfg = STATUS_CONFIG[item.status] ?? STATUS_CONFIG.open;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: `1px solid ${item.escalated ? 'var(--danger)' : 'var(--border)'}`,
      borderRadius: 8,
      marginBottom: 10,
      overflow: 'hidden',
    }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '14px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <Badge {...typeCfg} label={typeCfg.label} />
            <Badge {...statusCfg} label={item.status} />
            {item.escalated && (
              <Badge bg="var(--danger-subtle)" color="var(--danger)" label="ESCALATED" />
            )}
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              {item.votes.length} vote{item.votes.length !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              {item.comments.length} comment{item.comments.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{item.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {item.createdByName} ({item.createdBy}) · {fmtDate(item.createdAt)} ·{' '}
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>org:{item.orgId.slice(0, 8)}</span>
          </div>
        </div>
        <div style={{ color: 'var(--text-dim)', flexShrink: 0, marginTop: 2 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ paddingTop: 14, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
            {item.description}
          </div>

          {item.escalationNote && (
            <div style={{
              padding: '10px 12px',
              background: 'var(--danger-subtle)',
              border: '1px solid var(--danger)',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--danger)',
              marginBottom: 12,
            }}>
              <strong>Escalation note:</strong> {item.escalationNote}
            </div>
          )}

          {item.comments.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', marginBottom: 8, letterSpacing: '0.05em' }}>
                COMMENTS
              </div>
              {item.comments.map(c => (
                <div key={c.commentId} style={{
                  padding: '8px 12px',
                  background: 'var(--bg)',
                  borderRadius: 6,
                  marginBottom: 6,
                  fontSize: 12,
                }}>
                  <div style={{ color: 'var(--text-dim)', marginBottom: 4 }}>
                    {c.authorName} ({c.authorEmail}) · {fmtDate(c.createdAt)}
                  </div>
                  <div>{c.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FeedbackPage() {
  const [items, setItems] = useState<AdminFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [escalatedOnly, setEscalatedOnly] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    const url = `/admin/feedback${escalatedOnly ? '?escalated=true' : ''}`;
    api.get(url)
      .then(r => setItems(r.data.feedback))
      .catch(() => setError('Failed to load feedback'))
      .finally(() => setLoading(false));
  }, [escalatedOnly]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <MessageSquare size={18} color="var(--waiting)" />
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Feedback</h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={escalatedOnly}
            onChange={e => setEscalatedOnly(e.target.checked)}
          />
          Escalated only
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          {loading ? '…' : `${items.length} item${items.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: 'var(--danger-subtle)', border: '1px solid var(--danger)', borderRadius: 6, fontSize: 13, color: 'var(--danger)', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          No {escalatedOnly ? 'escalated ' : ''}feedback items.
        </div>
      ) : (
        items.map(item => <FeedbackRow key={item.feedbackId} item={item} />)
      )}
    </div>
  );
}
